import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Spinner, TextField, Input } from '@heroui/react';
import type { ReportPublicUser } from '@bunker/shared';
import { Button } from '@/components/Button';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { avatarHue } from '@/lib/avatarHue';
import { TrashIcon, UsersIcon } from '@/components/icons';
import { ReportQueue } from './ReportQueue';
import { BugReportQueue } from '@/components/beta/BugReportQueue';

/**
 * "Модерация Бункера" — the platform-level moderation surface (settings tab,
 * shown only to platform moderators). The queue covers out-of-server reports
 * (DMs, profiles) + critical escalations. Root admins additionally get the
 * team panel to assign/remove moderators without a redeploy.
 */
export function PlatformModerationPanel(props: { rootAdmin: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <div>
          <h3 className="text-sm font-medium text-ink">{t('mod.platformQueueTitle')}</h3>
          <p className="text-xs text-ink-muted">{t('mod.platformIntro')}</p>
        </div>
        <ReportQueue surface="platform" />
      </section>

      <section className="space-y-3 border-t border-separator pt-5">
        <h3 className="text-sm font-medium text-ink">{t('beta.queueTitle')}</h3>
        <BugReportQueue />
      </section>

      {props.rootAdmin && <ModerationTeamPanel />}
    </div>
  );
}

const teamKey = ['reports', 'moderators'] as const;

/** A Bunker user id (cuid): still accepted directly, as before. */
const USER_ID_RE = /^c[a-z0-9]{20,32}$/;

/** Round avatar for a team row: the picture or a tinted initial. */
function PersonAvatar({ user }: { user: ReportPublicUser }) {
  const hue = avatarHue(user.id);
  const label = user.displayName || user.username;
  return (
    <div
      className={
        'grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full text-xs ' +
        (user.avatarUrl ? 'text-ink' : `${hue.bg} ${hue.text}`)
      }
    >
      {user.avatarUrl ? (
        <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        label.charAt(0).toUpperCase()
      )}
    </div>
  );
}

/**
 * Pick the next moderator by name — the people search used elsewhere —
 * instead of pasting an internal id. A pasted id still works.
 */
function ModeratorPicker(props: {
  query: string;
  onQuery: (q: string) => void;
  pending: boolean;
  /** Already on the team (roots + moderators): shown, not offered. */
  teamIds: ReadonlySet<string>;
  onAdd: (userId: string) => void;
}) {
  const { t } = useTranslation();
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(props.query.trim()), 250);
    return () => clearTimeout(id);
  }, [props.query]);
  const trimmed = props.query.trim();
  const looksLikeId = USER_ID_RE.test(trimmed);
  const enabled = debounced.length >= 2 && !USER_ID_RE.test(debounced);
  const search = useQuery({
    queryKey: ['reports', 'moderator-search', debounced],
    queryFn: ({ signal }) => api.searchUsers(debounced, 6, signal),
    enabled,
    retry: 1,
  });
  const results = enabled ? search.data?.users ?? [] : [];

  let status: React.ReactNode = null;
  if (enabled && search.isError) {
    status = <p className="px-1 text-xs text-danger">{t('mod.team.searchError')}</p>;
  } else if (enabled && search.isFetching && results.length === 0) {
    status = (
      <div className="grid place-items-center py-2">
        <Spinner size="sm" />
      </div>
    );
  } else if (enabled && !search.isFetching && results.length === 0) {
    status = <p className="px-1 text-xs text-ink-muted">{t('mod.team.noResults')}</p>;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <TextField fullWidth aria-label={t('mod.team.searchLabel')}>
          <Input
            variant="secondary"
            value={props.query}
            onChange={(e) => props.onQuery(e.target.value)}
            placeholder={t('mod.team.searchPlaceholder')}
            aria-label={t('mod.team.searchLabel')}
          />
        </TextField>
        {looksLikeId && (
          <Button
            variant="primary"
            size="sm"
            className="min-h-11 shrink-0"
            disabled={props.pending || props.teamIds.has(trimmed)}
            onClick={() => props.onAdd(trimmed)}
          >
            {t('mod.team.addById')}
          </Button>
        )}
      </div>
      {status}
      {results.length > 0 && (
        <ul className="space-y-1" aria-label={t('mod.team.resultsLabel')}>
          {results.map((u) => {
            const label = u.displayName || u.username;
            return (
              <li key={u.id} className="flex items-center gap-2.5 rounded-lg px-3 py-1.5">
                <PersonAvatar user={u} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-ink">{label}</div>
                  <div className="truncate text-[11px] text-ink-muted">@{u.username}</div>
                </div>
                {props.teamIds.has(u.id) ? (
                  <span className="text-[11px] text-ink-muted">{t('mod.team.onTeam')}</span>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="min-h-11"
                    disabled={props.pending}
                    onClick={() => props.onAdd(u.id)}
                    aria-label={t('mod.team.addUser', { name: label })}
                  >
                    {t('mod.team.add')}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ModerationTeamPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [query, setQuery] = useState('');

  const q = useQuery({ queryKey: teamKey, queryFn: () => api.listModerators() });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: teamKey });
    void qc.invalidateQueries({ queryKey: ['reports', 'access'] });
  };

  const addM = useMutation({
    mutationFn: (id: string) => api.addModerator(id),
    onSuccess: () => {
      setQuery('');
      invalidate();
      toast.success(t('mod.team.added'));
    },
    onError: (err) => toast.error(...errToToast(err, t('mod.team.addError'))),
  });

  const removeM = useMutation({
    mutationFn: (id: string) => api.removeModerator(id),
    onSuccess: () => {
      invalidate();
      toast.success(t('mod.team.removed'));
    },
    onError: (err) => toast.error(...errToToast(err, t('mod.team.removeError'))),
  });

  const data = q.data;
  const teamIds = new Set<string>([
    ...(data?.rootAdminIds ?? []),
    ...(data?.moderators ?? []).map((m) => m.user.id),
  ]);

  return (
    <section className="space-y-3 border-t border-separator pt-5">
      <div className="flex items-center gap-2">
        <UsersIcon className="h-4 w-4 text-ink-muted" />
        <h3 className="text-sm font-medium text-ink">{t('mod.team.title')}</h3>
      </div>
      <p className="text-xs text-ink-muted">{t('mod.team.intro')}</p>

      <ModeratorPicker
        query={query}
        onQuery={setQuery}
        pending={addM.isPending}
        teamIds={teamIds}
        onAdd={(id) => addM.mutate(id)}
      />

      {q.isLoading ? (
        <div className="grid place-items-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Root admins — read-only (managed in env config). Shown as people;
              the raw id only when no such account exists. */}
          {(data?.rootAdminIds ?? []).map((id) => {
            const user = data?.rootAdmins?.find((u) => u.id === id);
            return (
              <div
                key={id}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2"
                style={{ backgroundColor: 'rgb(var(--surface-input) / var(--surface-input-alpha))' }}
              >
                {user ? (
                  <>
                    <PersonAvatar user={user} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-ink">{user.displayName || user.username}</div>
                      <div className="truncate text-[11px] text-ink-muted">@{user.username}</div>
                    </div>
                  </>
                ) : (
                  <span className="min-w-0 flex-1 truncate font-mono text-xs text-ink-dim">{id}</span>
                )}
                <span className="font-medium rounded-full bg-accent/12 px-2 py-0.5 text-[10px] text-accent">
                  {t('mod.team.rootBadge')}
                </span>
              </div>
            );
          })}

          {/* Assigned moderators — removable. */}
          {(data?.moderators ?? []).map((m) => {
            const label = m.user.displayName || m.user.username;
            return (
              <div
                key={m.user.id}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2"
              >
                <PersonAvatar user={m.user} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-ink">{label}</div>
                  <div className="truncate text-[11px] text-ink-muted">@{m.user.username}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={removeM.isPending}
                  onClick={() => removeM.mutate(m.user.id)}
                  aria-label={t('mod.team.removeUser', { name: label })}
                >
                  <TrashIcon className="h-4 w-4 text-danger" />
                </Button>
              </div>
            );
          })}

          {(data?.moderators?.length ?? 0) === 0 && (
            <p className="px-1 py-2 text-xs text-ink-muted">{t('mod.team.empty')}</p>
          )}
        </div>
      )}
    </section>
  );
}
